/**
 * Entitlement Tab API Layer
 *
 * This file contains RTK Query API definitions for entitlement operations:
 * - EntitlementTabType: Enum defining API slice name
 * - entitlementTabApi: Base API slice configuration
 * - entitlementTabApiEndpoints: Injected endpoints for entitlement operations
 * - getEntitlementList: Mutation for fetching SOD entitlement data from ECMv6
 * - useGetEntitlementListMutation: React hook for entitlement list operations
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../shared/services/baseQuery';
import { type InsightItemTypes } from '../../../shared/types';
import { type GetColumnPickerApiResponse } from '../../ViolationsTab/data-layer/violationsTabTypes';

import {
  type GetEntitlementInsightsApiResponse,
  type GetEntitlementListApiResponse,
  type SodEntitlementListProps,
} from './entitlementListTypes';

export enum EntitlementTabType {
  API_SLICE_NAME = 'aag/entitlementTabApi',
}

export const entitlementTabApi = createApi({
  reducerPath: EntitlementTabType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  endpoints: () => ({}),
});

// API endpoints for entitlement tab operations
export const entitlementTabApiEndpoints = entitlementTabApi.injectEndpoints({
  endpoints: (builder) => ({
    getEntitlementList: builder.mutation<unknown, SodEntitlementListProps>({
      query: (payload) => ({
        url: '/api/sodms/inherent-risk/search',
        method: 'POST',
        data: payload,
        isECMv6: true,
        config: {
          timeout: 30000, // 30 second timeout
        },
      }),
      transformResponse: (response: GetEntitlementListApiResponse) => {
        if (response.status === 200) {
          return {
            data: response.data,
            totalRecords: response.totalRecords,
            displayRecords: response.displayRecords,
            page: response.page,
            sort: response.sort,
          };
        }

        throw new Error('Invalid API response');
      },
      transformErrorResponse: (response: GetEntitlementListApiResponse) => ({
        status: response.status,
        data: response.data,
      }),
    }),
    getEntColumnPicker: builder.query<unknown, void>({
      query: () => ({
        url: '/api/sodms/view/columns',
        params: {
          view: 'inherent_entitlement',
        },
        isECMv6: true,
        config: {
          timeout: 30000, // 30 second timeout
        },
      }),
      transformResponse: (response: GetColumnPickerApiResponse) => {
        if (response.status === 200) {
          return response.data;
        }

        throw new Error('Invalid column picker response');
      },
      transformErrorResponse: (response: GetColumnPickerApiResponse) => ({
        status: response.status,
        data: response.data,
      }),
    }),
    getEntitlementInsights: builder.query<InsightItemTypes[], unknown>({
      query: (payload) => ({
        url: '/api/sodms/inherent-risk/insights/search',
        method: 'POST',
        data: payload,
        isECMv6: true,
        config: {
          timeout: 30000, // 30 second timeout
        },
      }),
      transformResponse: (response: GetEntitlementInsightsApiResponse) => {
        if (Array.isArray(response.data)) {
          return response.data;
        }

        throw new Error('Invalid insights response');
      },
      transformErrorResponse: (
        response: GetEntitlementInsightsApiResponse
      ) => ({
        status: response.status,
        data: response.data,
      }),
    }),
  }),
});

export const {
  useGetEntitlementListMutation,
  useLazyGetEntColumnPickerQuery,
  useLazyGetEntitlementInsightsQuery,
} = entitlementTabApiEndpoints;

export default entitlementTabApiEndpoints;
